import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { StorageService } from '@core/services/storages.service';
import { AuthStateService } from '@core/services/auth-state.service';
import { decodeJwtPayload } from '@core/utils/jwt-decoder';
import { SESSION_ACCESS_KEY } from '@core/constants/token-storage.constants';
import { TrunkBaseConfigurationService } from '@core/services/trunk-base-configuration/trunk-base-configuration.service';

/**
 * Initializes AuthStateService before the configuration-ia module loads.
 * Never blocks navigation — token capture is handled by UrlTokenService.
 */
export const urlTokenGuard: CanActivateFn = () => {
  if (inject(TrunkBaseConfigurationService).CTDLegacyTokenOnly) return true;

  const storageService = inject(StorageService);
  const authState      = inject(AuthStateService);

  // Already initialized
  if (authState.user() !== null) return true;

  // 1. Try sessionStorage (populated by main.ts IIFE)
  const raw         = storageService.getSessionStorage(SESSION_ACCESS_KEY);
  const fromStorage = typeof raw === 'string' && raw ? raw : null;
  if (fromStorage && decodeJwtPayload(fromStorage) !== null) {
    authState.setFromToken(fromStorage);
    return true;
  }

  // 2. Fallback: read from URL hash
  const hash        = window.location.hash;
  const queryString = hash.includes('?') ? hash.split('?')[1] : '';
  const fromUrl     = new URLSearchParams(queryString).get('accessToken');
  if (fromUrl && decodeJwtPayload(fromUrl) !== null) {
    storageService.setSessionStorage(SESSION_ACCESS_KEY, fromUrl);
    authState.setFromToken(fromUrl);
    return true;
  }

  // No token found — allow navigation anyway, UrlTokenService will handle capture
  return true;
};
